import { useRef, useState } from "react";
import { X, Check } from "lucide-react";
import { COUNTRIES, COUNTRY_ORDER, guessCountry, TAX_FIGURES_CHECKED } from "./countries.js";

// First-run question (and "change country" in Settings). The currency is chosen here too, inside the country. Everything on
// the Tax help screen follows the country choice.
export function CountrySheet({ current, currentCurrency, currencies = [], hasEntries, showTax = true, onSave, onSkip }) {
  const [pick, setPick] = useState(current || guessCountry());
  const [currency, setCurrency] = useState(currentCurrency || COUNTRIES[current || guessCountry()]?.currency || "USD");
  const touched = useRef(false);   // once the person picks a currency themselves, choosing another country stops overriding it
  const chooseCountry = (code) => {
    setPick(code);
    const cur = COUNTRIES[code]?.currency;
    if (!touched.current && !hasEntries && cur) setCurrency(cur);   // follow the country, unless amounts already exist
  };
  const changed = currency !== currentCurrency;
  return (
    <div className="overlay-backdrop fixed inset-0 z-50 flex items-end lg:items-center justify-center" role="dialog" aria-modal="true" aria-label={showTax ? "Where do you pay tax?" : "Where are you based?"}>
      <div className="bg-card2 rounded-t-3xl lg:rounded-3xl w-full max-w-sm md:max-w-md p-5" style={{ maxHeight: "92vh", overflowY: "auto" }}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="v-display text-lg font-semibold">{showTax ? "Where do you pay tax?" : "Where are you based?"}</h3>
          {onSkip && <button onClick={onSkip} aria-label="Not now"><X size={18} className="text-ivory-dim" /></button>}
        </div>
        <p className="text-xs text-ivory-dim mb-4 leading-relaxed">{showTax ? "We'll show the tax rates, thresholds and deadlines for your country in Tax help, and use your country's mileage rules where there is one." : "We'll use your country to suggest the right currency and local defaults."}</p>

        <div className="flex flex-col gap-1.5 mb-4" role="radiogroup" aria-label="Country">
          {COUNTRY_ORDER.map((code) => {
            const c = COUNTRIES[code]; const on = pick === code;
            return (
              <button key={code} role="radio" aria-checked={on} onClick={() => chooseCountry(code)} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left" style={{ background: on ? "var(--brass-dim)" : "var(--card)", boxShadow: on ? "inset 0 0 0 1.5px var(--brass)" : "none" }}>
                <span className="text-lg w-6 text-center" aria-hidden="true">{c.flag}</span>
                <span className="flex-1 min-w-0"><span className="block text-sm font-medium">{c.name}</span>
                  <span className="block text-xs text-ivory-dim">{c.currency || "Choose your currency below"}{showTax ? (c.detailed ? " · detailed tax numbers" : " · simple set-aside only") : ""}</span></span>
                {on && <Check size={16} className="text-brass shrink-0" />}
              </button>
            );
          })}
        </div>

        {currencies.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold mb-1.5">Currency</div>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Currency">
              {currencies.map((c) => {
                const on = currency === c.code;
                return (
                  <button key={c.code} role="radio" aria-checked={on} onClick={() => { touched.current = true; setCurrency(c.code); }} className="rounded-lg py-2 text-xs font-medium text-center"
                    style={{ background: on ? "var(--brass)" : "var(--card)", color: on ? "var(--ink)" : "var(--ivory-dim)" }}>{c.symbol} {c.code}</button>
                );
              })}
            </div>
            <div className="text-xs text-ivory-dim mt-2 leading-relaxed">
              {changed && hasEntries ? "Only the symbol changes. Amounts you've already entered stay as they are, so pick the currency they're in." : "Used for this profile; each profile can have its own."}
            </div>
          </div>
        )}

        {showTax && (
          <div className="text-xs text-ivory-dim rounded-xl px-3 py-2.5 mb-4 leading-relaxed" style={{ background: "var(--brass-dim)" }}>
            Tax help is for planning only and is not tax advice. Rates and deadlines change; figures were last checked {TAX_FIGURES_CHECKED}. Please confirm with a tax professional or your tax authority before you pay or file.
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={() => onSave({ country: pick, currency })} className="flex-1 bg-gold-grad text-ink font-semibold rounded-full py-3 text-sm">Save</button>
          {onSkip && <button onClick={onSkip} className="px-4 text-xs text-ivory-dim">Not now</button>}
        </div>
      </div>
    </div>
  );
}
